import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "@/components/MarketingLayout";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import * as Lucide from "lucide-react";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [yearly, setYearly] = useState(false);
  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  return (
    <MarketingLayout>
      <Seo title="Pricing" path="/pricing" description="Simple, transparent credit-based pricing for BINAREX.CO. Starter, Growth, Business, Agency, and Enterprise plans." />
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-10 text-center">
        <div className="bx-chip mx-auto"><Lucide.BadgePercent size={12} /> PRICING PLANS</div>
        <h1 className="bx-heading mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">Simple, Transparent Pricing</h1>
        <p className="text-slate-400 mt-4 max-w-2xl mx-auto">Choose the perfect credit plan that fits your needs. Use credits across all 25+ AI tools.</p>
        <div className="mt-7 inline-flex p-1 rounded-full bg-white/5 border border-white/10">
          <button onClick={() => setYearly(false)} className={`px-4 py-1.5 rounded-full text-sm ${!yearly ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white" : "text-slate-300"}`}>Monthly</button>
          <button onClick={() => setYearly(true)} className={`px-4 py-1.5 rounded-full text-sm ${yearly ? "bg-gradient-to-r from-purple-600 to-blue-600 text-white" : "text-slate-300"}`}>Yearly <span className="text-xs">(Save 20%)</span></button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {plans.map((p) => {
            const price = yearly ? (p.price_usd * 12 * 0.8).toFixed(2) : p.price_usd.toFixed(2);
            return (
              <div key={p.id} data-testid={`pricing-${p.slug}`} className={`relative bx-card p-6 flex flex-col ${p.popular ? "border-purple-500/60 shadow-[0_30px_80px_-20px_rgba(139,92,246,0.45)]" : ""}`}>
                {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bx-chip">Most Popular</div>}
                <div className="bx-heading text-lg font-semibold">{p.name.replace(" Credits", "")}</div>
                <div className="text-xs text-slate-400 mt-0.5">{p.best_for}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="bx-heading text-4xl font-extrabold">${price}</span>
                  <span className="text-slate-400 text-sm">/{yearly ? "yr" : "mo"}</span>
                </div>
                <div className="mt-1 text-purple-300 font-mono text-sm">{p.credits.toLocaleString()} credits</div>
                <div className="mt-0.5 text-xs text-slate-500">Valid for {p.validity_days} days</div>
                <ul className="mt-5 space-y-2 text-sm flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2"><Lucide.Check size={14} className="text-emerald-400 mt-0.5" /> <span className="text-slate-300">{f}</span></li>
                  ))}
                </ul>
                <Link to={`/buy-credits?plan=${p.slug}`} data-testid={`pricing-buy-${p.slug}`} className={`mt-6 w-full justify-center ${p.popular ? "bx-btn-primary" : "bx-btn-ghost"}`}>Buy Now</Link>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
          {[
            [Lucide.ShieldCheck, "No Hidden Fees", "What you see is what you pay."],
            [Lucide.Lock, "Secure Payment", "100% safe & secure"],
            [Lucide.Zap, "Instant Credits", "Get credits instantly after payment."],
            [Lucide.RotateCcw, "30-Day Money Back", "Not satisfied? Get a full refund."],
          ].map(([Cmp, t, d], idx) => (
            <div key={idx} className="bx-card p-4 flex items-start gap-3">
              <span className={`bx-glow-icon ${["", "cyan", "blue", "emerald"][idx % 4]}`}><Cmp size={18} /></span>
              <div>
                <div className="font-semibold">{t}</div>
                <div className="text-xs text-slate-400 mt-0.5">{d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}
